import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { financeApi, Receivable } from '../../api/finance';
import { masterDataApi } from '../../api/masterData';
import { projectsApi } from '../../api/projects';
import { ApiError } from '../../api/client';
import { SectionHeader } from '../../components/SectionHeader';
import { PageHeader } from '../../components/PageHeader';
import { StatusBadge } from '../../components/StatusBadge';
import { IconActionButton } from '../../components/IconActionButton';
import { Eye } from 'lucide-react';
import { DataTable } from '../../components/DataTable';
import { useToast } from '../../components/Toast';

const STATUS_LABELS: Record<string, string> = { outstanding: 'Outstanding', partially_collected: 'Partially Collected', collected: 'Collected' };

/** Real, honest scoping: no upstream module produces receivables automatically yet (no Sales/pledge-tracking module exists) - these are entered directly, most commonly a donor pledge or grant instalment not yet received. */
export function ReceivablesPage() {
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [showForm, setShowForm] = useState(false);

  const receivablesQuery = useQuery({ queryKey: ['finance-receivables'], queryFn: () => financeApi.listReceivables() });
  const donorsQuery = useQuery({ queryKey: ['master-donors'], queryFn: () => masterDataApi.donors(), enabled: showForm });
  const projectsQuery = useQuery({ queryKey: ['projects-list'], queryFn: () => projectsApi.list(), enabled: showForm });
  const grantsQuery = useQuery({ queryKey: ['finance-grants'], queryFn: () => financeApi.listGrants(), enabled: showForm });

  const { register, handleSubmit, reset } = useForm<Record<string, string>>();
  const invalidate = () => queryClient.invalidateQueries({ queryKey: ['finance-receivables'] });

  const createMutation = useMutation({
    mutationFn: (v: Record<string, string>) => financeApi.createReceivable({
      description: v.description, donorId: v.donorId ? Number(v.donorId) : undefined, projectId: v.projectId ? Number(v.projectId) : undefined,
      grantId: v.grantId ? Number(v.grantId) : undefined, amount: Number(v.amount), dueDate: v.dueDate || undefined,
    }),
    onSuccess: (result) => { showSuccess(`Receivable created - ${result.data.receivableReference}`); invalidate(); setShowForm(false); reset(); },
    onError: (err) => showError(err instanceof ApiError ? err.message : 'Failed to create receivable.'),
  });

  return (
    <div className="page">
      <PageHeader title="Accounts Receivable" actions={<button className="btn-primary" onClick={() => setShowForm((v) => !v)}>{showForm ? 'Cancel' : 'New Receivable'}</button>} />
      <p className="muted">Amounts owed to Verdantrise - most commonly a donor pledge or grant instalment not yet received.</p>

      {showForm && (
        <section className="card">
          <h2>New Receivable</h2>
          <form onSubmit={handleSubmit((v) => createMutation.mutate(v))}>
            <label>Description<input {...register('description', { required: true })} /></label>
            <label>
              Donor (optional)
              <select {...register('donorId')}>
                <option value="">- none -</option>
                {donorsQuery.data?.data.map((d) => <option key={d.id} value={d.id}>{d.name}</option>)}
              </select>
            </label>
            <label>
              Project (optional)
              <select {...register('projectId')}>
                <option value="">- none -</option>
                {projectsQuery.data?.data.map((p) => <option key={p.id} value={p.id}>{p.project_name}</option>)}
              </select>
            </label>
            <label>
              Grant (optional)
              <select {...register('grantId')}>
                <option value="">- none -</option>
                {grantsQuery.data?.data.map((g) => <option key={g.id} value={g.id}>{g.grant_reference} - {g.grant_name}</option>)}
              </select>
            </label>
            <label>Amount<input type="number" step="0.01" {...register('amount', { required: true })} /></label>
            <label>Due Date (optional)<input type="date" {...register('dueDate')} /></label>
            <div className="form-actions"><button type="submit" className="btn-primary" disabled={createMutation.isPending}>{createMutation.isPending ? 'Creating…' : 'Create'}</button><button type="button" className="btn-secondary" onClick={() => { setShowForm(false); reset(); }}>Cancel</button></div>
          </form>
        </section>
      )}

      <div className="panel">
        <SectionHeader title="All Receivables" />
        <div className="panel-body">
        {receivablesQuery.data && (
          <DataTable<Receivable>
            rows={receivablesQuery.data.data}
            rowKey={(r) => r.id}
            emptyMessage="No receivables yet."
            columns={[
              { header: 'Reference', render: (r) => r.receivable_reference },
              { header: 'Description', render: (r) => r.description },
              { header: 'Donor', render: (r) => r.donor_name ?? '-' },
              { header: 'Amount', render: (r) => Number(r.amount).toLocaleString() },
              { header: 'Collected', render: (r) => Number(r.amount_collected).toLocaleString() },
              { header: 'Outstanding', render: (r) => (Number(r.amount) - Number(r.amount_collected)).toLocaleString() },
              { header: 'Status', render: (r) => <StatusBadge status={r.status} label={STATUS_LABELS[r.status]} /> },
              { header: '', render: (r) => <IconActionButton icon={Eye} label="View" onClick={() => navigate(`/finance/receivables/${r.id}`)} /> },
            ]}
          />
        )}
      </div>
      </div>
    </div>
  );
}
